import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { fetchPollLoanFilter, updatePollLoanFilter } from "./api";

/**
 * The eDoc poll's first-layer loan filter.
 *
 * Sits above the flag rows because it reads with them: `enableNewfiSplitEdocPoll`
 * decides whether the 5-minute poll runs at all, this decides which loans it is
 * allowed to act on, and the two are independent switches. Turning the poll on
 * with the filter still narrowed to a test loan is the intended way to exercise
 * this feature against production.
 *
 * The reason it is not a flag row: this is the control that stands between the
 * poll and every live loan in Newfi's MeridianLink instance. A detected package
 * is imported, extracted, split and eventually written back into a real lender's
 * file, with no human step in between — so widening this is the single most
 * consequential change anybody can make from this page, and it should not look
 * like a toggle.
 */

const MODE_ALL = "all";
const MODE_SPECIFIC = "specific_loans";

/**
 * Split a textarea into loan numbers.
 *
 * Newlines, commas, semicolons and tabs all separate, because the list arrives
 * pasted out of a spreadsheet as often as it is typed. Spaces do not: a loan
 * number containing one would be silently cut in half, and half a loan number
 * matches nothing.
 */
function parseLoanNumbers(text) {
  return String(text || "")
    .split(/[\n,;\t]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

/** De-duplicated case-insensitively, matching what the backend stores. */
function dedupe(loanNumbers) {
  const seen = new Set();
  return loanNumbers.filter((entry) => {
    const key = entry.toUpperCase();
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

const sameList = (a, b) =>
  a.length === b.length &&
  a.every((entry, index) => entry.toUpperCase() === b[index].toUpperCase());

export default function PollLoanFilterCard({ env }) {
  const [saved, setSaved] = useState(null);
  const [mode, setMode] = useState(MODE_SPECIFIC);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchPollLoanFilter(env)
      .then((res) => {
        if (!res?.success) {
          setError(res?.reason || "The backend did not return the filter.");
          return;
        }
        const filter = res.filter || {};
        const loanNumbers = filter.loanNumbers || [];
        setSaved({ ...filter, loanNumbers });
        setMode(filter.mode === MODE_ALL ? MODE_ALL : MODE_SPECIFIC);
        setText(loanNumbers.join("\n"));
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [env]);

  useEffect(load, [load]);

  const draftLoanNumbers = useMemo(
    () => dedupe(parseLoanNumbers(text)),
    [text],
  );

  const dirty =
    !!saved &&
    (mode !== saved.mode ||
      !sameList(draftLoanNumbers, saved.loanNumbers || []));

  const onSave = async () => {
    // Two different confirmations on purpose. Narrowing the list is routine and
    // gets the ordinary "which environment am I in" check; widening to every
    // loan is the one action on this page that can reach a live borrower's
    // file, so it names the consequence instead of the setting.
    const confirmed =
      mode === MODE_ALL
        ? window.confirm(
            `${env.toUpperCase()}: allow the eDoc poll to act on EVERY loan?\n\n` +
              "While enableNewfiSplitEdocPoll is on, every indexing package " +
              "uploaded to any Newfi loan in this environment will be " +
              "detected, imported, split and pushed back into the lender's " +
              "MeridianLink file automatically. There is no confirmation step " +
              "after this one.\n\n" +
              "Only do this when the feature is meant to be live for real " +
              "production traffic.",
          )
        : window.confirm(
            `${env.toUpperCase()}: restrict the eDoc poll to ${draftLoanNumbers.length} loan number(s)?\n\n` +
              (draftLoanNumbers.length === 0
                ? "The list is empty, so the poll will detect nothing at all."
                : `${draftLoanNumbers.join("\n")}\n\nEvery other loan is ignored before any record is created.`),
          );
    if (!confirmed) {
      return;
    }

    setSaving(true);
    try {
      const res = await updatePollLoanFilter(
        { mode, loanNumbers: draftLoanNumbers },
        env,
      );
      if (!res?.success) {
        throw new Error(res?.reason || "The backend rejected the change.");
      }
      // Render what was stored, not what was asked for: the backend trims,
      // de-duplicates and can reject, and the textarea should show the result.
      const filter = res.filter || {};
      const loanNumbers = filter.loanNumbers || [];
      setSaved({ ...filter, loanNumbers });
      setMode(filter.mode === MODE_ALL ? MODE_ALL : MODE_SPECIFIC);
      setText(loanNumbers.join("\n"));
      toast.success(
        filter.mode === MODE_ALL
          ? `The eDoc poll now acts on every loan in ${env}.`
          : `The eDoc poll is restricted to ${loanNumbers.length} loan number(s) in ${env}.`,
      );
    } catch (err) {
      toast.error(`Could not save the poll loan filter: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const onReset = () => {
    if (!saved) {
      return;
    }
    setMode(saved.mode === MODE_ALL ? MODE_ALL : MODE_SPECIFIC);
    setText((saved.loanNumbers || []).join("\n"));
  };

  const savedIsAll = saved?.mode === MODE_ALL;
  const savedCount = saved?.loanNumbers?.length || 0;

  return (
    <section className="nse-card nse-pollfilter">
      <div className="nse-card__head">
        <h2 className="nse-card__title">Poll loan filter</h2>
        {saved && (
          <span
            className={`nse-pill ${savedIsAll ? "nse-pill--failed" : "nse-pill--ready"}`}
          >
            {savedIsAll
              ? "Every loan"
              : `${savedCount} loan number${savedCount === 1 ? "" : "s"}`}
          </span>
        )}
      </div>

      <p className="nse-flag-group__note">
        The first thing the 5-minute poll checks, before it decides whether a
        modified eDoc is even a package. A document on a loan outside this
        filter is dropped there and then — no tracker record, no import, no
        trace. This is independent of the poll flag below: the poll can be on
        while this still admits only a test loan, which is how this feature is
        meant to be exercised against production.
      </p>

      {loading && <div className="nse-state">Loading…</div>}

      {error && !loading && (
        <div className="nse-notice nse-notice--error">
          <strong>Could not read the filter.</strong>
          <pre className="nse-error-detail">{error}</pre>
          <p className="nse-pollfilter__errnote">
            While it is unreadable the poll skips its tick entirely rather than
            guessing which loans are allowed, so nothing is being detected in{" "}
            {env}.
          </p>
          <button type="button" className="nse-btn" onClick={load}>
            Retry
          </button>
        </div>
      )}

      {saved && !loading && !error && (
        <>
          {saved.isSetInDatabase === false && (
            <div className="nse-notice nse-notice--warn">
              <strong>Never configured in {env}.</strong> There is no{" "}
              <code>whereis/newfi-split-edoc-poll-filter</code> document here, so
              the poll is treating every loan as not allowed. That is the
              fail-closed default — save a list below to make the intent a fact.
            </div>
          )}

          {savedIsAll && (
            <div className="nse-notice nse-notice--error">
              <strong>Every loan is in scope in {env}.</strong> The moment{" "}
              <code>enableNewfiSplitEdocPoll</code> is on, packages on real
              customer loans are detected and pushed back to MeridianLink
              automatically.
            </div>
          )}

          {!savedIsAll && savedCount === 0 && (
            <div className="nse-notice nse-notice--warn">
              <strong>The allowed list is empty.</strong> The poll runs and
              detects nothing. This is a valid configuration, not a failure —
              but if you are waiting on a package, this is why it never arrived.
            </div>
          )}

          <div className="nse-pollfilter__modes" role="radiogroup">
            <label
              className={`nse-pollfilter__mode ${mode === MODE_SPECIFIC ? "is-active" : ""}`}
            >
              <input
                type="radio"
                name="nse-poll-filter-mode"
                value={MODE_SPECIFIC}
                checked={mode === MODE_SPECIFIC}
                onChange={() => setMode(MODE_SPECIFIC)}
              />
              <span>
                <strong>Only these loan numbers</strong>
                <span className="nse-muted">
                  Everything else is ignored before any record is created.
                </span>
              </span>
            </label>

            <label
              className={`nse-pollfilter__mode nse-pollfilter__mode--danger ${mode === MODE_ALL ? "is-active" : ""}`}
            >
              <input
                type="radio"
                name="nse-poll-filter-mode"
                value={MODE_ALL}
                checked={mode === MODE_ALL}
                onChange={() => setMode(MODE_ALL)}
              />
              <span>
                <strong>Any loan</strong>
                <span className="nse-muted">
                  No filtering. Every loan in the organization&rsquo;s
                  modified-eDoc feed is eligible.
                </span>
              </span>
            </label>
          </div>

          <label className="nse-pollfilter__label" htmlFor="nse-poll-loans">
            Allowed loan numbers
            <span className="nse-muted">
              {" "}
              — one per line (commas and semicolons also separate). Matching
              ignores case and surrounding spaces.
            </span>
          </label>
          <textarea
            id="nse-poll-loans"
            className="nse-input nse-pollfilter__textarea"
            rows={6}
            spellCheck={false}
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={"CC2026071957-Test-L7"}
          />

          <div className="nse-pollfilter__foot">
            <span className="nse-muted">
              {mode === MODE_ALL ? (
                <>
                  The list is kept but not applied while the mode is{" "}
                  <span className="nse-mono">all</span>.
                </>
              ) : (
                <>
                  {draftLoanNumbers.length} loan number
                  {draftLoanNumbers.length === 1 ? "" : "s"} will be allowed.
                </>
              )}
            </span>
            <div className="nse-pollfilter__actions">
              {dirty && (
                <button
                  type="button"
                  className="nse-btn"
                  onClick={onReset}
                  disabled={saving}
                >
                  Discard changes
                </button>
              )}
              <button
                type="button"
                className="nse-btn nse-btn--primary"
                onClick={onSave}
                disabled={saving || !dirty}
                title={
                  dirty
                    ? `Write the filter to ${env}`
                    : "Nothing has changed yet"
                }
              >
                {saving ? "Saving…" : "Save filter"}
              </button>
            </div>
          </div>

          <div className="nse-flag__meta">
            <span
              className="nse-mono"
              title="Firestore document backing this control"
            >
              whereis/newfi-split-edoc-poll-filter
            </span>
            <span className="nse-muted">
              stored mode: {saved.mode} · {saved.summary}
            </span>
          </div>
        </>
      )}
    </section>
  );
}
